import { Link } from "react-router-dom";
import { Waves, Mountain, LifeBuoy, Radio, Mail, ShieldAlert, Compass, ArrowRight } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const Logo = () => (
  <img
    src="/logo-proteccion-civil-vigo.png"
    alt="Protección Civil Concello de Vigo"
    className="w-16 h-16 object-contain shrink-0"
  />
);

export default function ProteccionCivilPage() {
  return (
    <div data-testid="proteccion-civil-page" className="min-h-screen bg-[#fffbf2] text-[#0f2027]">
      <header className="sticky top-0 z-40 bg-[#fffbf2]/95 backdrop-blur-md border-b border-[#0f2027]/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="pc-brand">
            <BrandMark />
            <div className="leading-none">
              <span className="block font-display text-sm tracking-wider uppercase text-[#0f2027]">PROTECCIÓN CIVIL VIGO</span>
              <span className="block text-[10px] tracking-widest text-[#e65100] font-mono-tactical uppercase">
                Teleformación & Rescate
              </span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-xs font-mono-tactical uppercase tracking-widest text-[#0f2027]/80">
            <Link to="/" className="hover:text-[#e65100] transition-colors">Inicio</Link>
            <Link to="/#cursos" className="hover:text-[#e65100] transition-colors">Catálogo</Link>
          </div>
          <Link
            to="/#contacto"
            data-testid="pc-contact-btn"
            className="bg-[#e65100] text-white px-4 py-2 text-xs font-mono-tactical uppercase tracking-wider font-semibold rounded hover:bg-[#c2410c] transition-colors"
          >
            Inscribirse
          </Link>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-20 md:py-24 px-6 border-b border-[#0f2027]/10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#e65100]/10 text-[#e65100] text-xs font-mono-tactical uppercase tracking-widest">
                <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" />
                Qué es Protección Civil Vigo
              </span>
              <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.95] tracking-tight">
                Voluntariado que responde cuando más importa
              </h1>
              <p className="text-base md:text-lg text-[#0f2027]/80 leading-relaxed max-w-2xl">
                Protección Civil en Vigo es el sistema de apoyo voluntario que respalda a los servicios de emergencia
                ante catástrofes, accidentes y situaciones de riesgo en la ciudad y su entorno. Formamos a las personas
                que intervienen cuando más se necesita: en la mar, en la montaña y en la búsqueda de personas
                desaparecidas.
              </p>
            </div>
            <div className="lg:col-span-5 flex justify-center">
              <div className="p-8 bg-white border-2 border-[#0f2027] rounded-full shadow-[8px_8px_0px_#e65100]">
                <Logo />
              </div>
            </div>
          </div>
        </section>

        {/* Salvamento Marítimo */}
        <section className="py-20 px-6 bg-[#f7eedc] border-b border-[#0f2027]/10" data-testid="pc-salvamento">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="p-10 bg-[#0f2027] rounded-lg shadow-[8px_8px_0px_#e65100] text-center">
                <Waves className="w-16 h-16 text-[#e65100] mx-auto" aria-hidden="true" />
              </div>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">Especialidad 01</span>
              <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Salvamento Marítimo</h2>
              <p className="text-sm md:text-base text-[#0f2027]/80 leading-relaxed">
                Intervención en rescate acuático en la ría de Vigo y la costa gallega: aproximación a la víctima,
                manejo de material de flotación, señalización y coordinación con los servicios de emergencia
                marítima.
              </p>
              <Link
                to="/#contacto"
                className="inline-flex items-center gap-2 bg-[#0f2027] text-white px-6 py-3 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#e65100] transition-colors"
                data-testid="pc-salvamento-cta"
              >
                Solicitar plaza
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* Búsqueda de Personas */}
        <section className="py-20 px-6 bg-[#fffbf2]" data-testid="pc-busqueda">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-5 order-2 lg:order-1">
              <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">Especialidad 02</span>
              <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Búsqueda de Personas</h2>
              <p className="text-sm md:text-base text-[#0f2027]/80 leading-relaxed">
                Metodología de búsqueda de personas desaparecidas en terreno abierto: batidas organizadas,
                orientación, lectura del terreno y coordinación de equipos de voluntarios con las fuerzas de
                seguridad.
              </p>
              <Link
                to="/#contacto"
                className="inline-flex items-center gap-2 bg-[#0f2027] text-white px-6 py-3 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#e65100] transition-colors"
                data-testid="pc-busqueda-cta"
              >
                Solicitar plaza
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="p-10 bg-[#0f2027] rounded-lg shadow-[8px_8px_0px_#0f2027] text-center">
                <Compass className="w-16 h-16 text-[#e65100] mx-auto" aria-hidden="true" />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#0f2027] text-[#fffbf2]/60 py-8 px-6 text-xs font-mono-tactical border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="uppercase tracking-wider text-white">Protección Civil Concello de Vigo</span>
          <a href="mailto:info@busquedaysalvamento.es" className="flex items-center gap-2 text-[#e65100] hover:underline">
            <Mail className="w-3.5 h-3.5" aria-hidden="true" />
            info@busquedaysalvamento.es
          </a>
          <span className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-[#e65100]" aria-hidden="true" />
            Plataforma de Teleformación Oficial · Galicia
          </span>
        </div>
      </footer>
    </div>
  );
}
